import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAPI } from '../../hooks/useAPI';
import { uploadImage } from '../../utils/uploadFile';
import Notification from '../../components/Notification';
import ConfirmDialog from '../../components/ConfirmDialog';
import APP_CONFIG from '../../config/appConfig';
import { ProtectedRoute } from '../../utils/authGuard';

const ManagePoetry = () => {
  const { data: poems, loading, addItem, updateItem, deleteItem } = useAPI('poems');
  
  // Ensure poems is always an array
  const safePoems = Array.isArray(poems) ? poems : [];
  
  const [showForm, setShowForm] = useState(false);
  const [editingPoem, setEditingPoem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    poem: null,
    title: '',
    message: '',
    confirmText: 'Delete',
    cancelText: 'Cancel'
  });
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    category: '',
    image: null
  });

  // Character limits
  const limits = {
    title: 80,
    author: 50,
    content: 3000
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const limit = limits[name];
    
    if (limit && value.length > limit) {
      return; // Don't update if over limit
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleImageChange = (e) => {
    setFormData({
      ...formData,
      image: e.target.files[0]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let imageUrl = '';
      if (formData.image) {
        imageUrl = await uploadImage(formData.image, 'poetry');
      } else if (editingPoem && editingPoem.image) {
        imageUrl = editingPoem.image;
      }

      const poemData = {
        ...formData,
        image: imageUrl,
        createdAt: editingPoem ? editingPoem.createdAt : new Date()
      };

      if (editingPoem) {
        const poemId = editingPoem.id || editingPoem._id;
        await updateItem(poemId, poemData);
      } else {
        await addItem(poemData);
      }

      setNotification({ message: editingPoem ? 'Poem updated successfully!' : 'Poem added successfully!', type: 'success' });
      setFormData({ title: '', content: '', author: '', category: '', image: null });
      setShowForm(false);
      setEditingPoem(null);
    } catch (error) {
      setNotification({ message: 'Failed to save poem. Please try again.', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (poem) => {
    setEditingPoem(poem);
    setFormData({
      title: poem.title,
      content: poem.content,
      author: poem.author,
      category: poem.category,
      image: null
    });
    setIsSubmitting(false);
    setShowForm(true);
  };

  const handleDelete = async (poem) => {
    setConfirmDialog({
      isOpen: true,
      poem: poem,
      title: 'Delete Poem',
      message: `Are you sure you want to delete "${poem.title}"? This action cannot be undone.`
    });
  };

  const confirmDelete = async () => {
    try {
      const poemId = confirmDialog.poem.id || confirmDialog.poem._id;
      await deleteItem(poemId);
      setNotification({ message: 'Poem deleted successfully!', type: 'success' });
    } catch (error) {
      setNotification({ message: 'Failed to delete poem. Please try again.', type: 'error' });
    } finally {
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  const cancelDelete = () => {
    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    const baseServerUrl = APP_CONFIG.getBaseServerUrl();
    return `${baseServerUrl}${image}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-black">Manage Poetry</h1>
              <p className="text-black mt-1 sm:mt-2 text-sm sm:text-base">Create and manage your poetry collection</p>
            </div>
            <button
              onClick={() => {
                setShowForm(true);
                setIsSubmitting(false);
              }}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base whitespace-nowrap"
            >
              Add New Poem
            </button>
          </div>

          {showForm && (
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-black mb-3 sm:mb-4">
                {editingPoem ? 'Edit Poem' : 'Add New Poem'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Title
                      <span className="text-gray-600 text-xs ml-2">
                        ({formData.title.length}/{limits.title})
                      </span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      maxLength={limits.title}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 text-black ${
                        formData.title.length > limits.title * 0.9 
                          ? 'border-yellow-400 bg-yellow-50' 
                          : 'border-gray-300'
                      }`}
                      placeholder="Enter poem title..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Author
                      <span className="text-gray-600 text-xs ml-2">
                        ({formData.author.length}/{limits.author})
                      </span>
                    </label>
                    <input
                      type="text"
                      name="author"
                      value={formData.author}
                      onChange={handleChange}
                      required
                      maxLength={limits.author}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 text-black ${
                        formData.author.length > limits.author * 0.9 
                          ? 'border-yellow-400 bg-yellow-50' 
                          : 'border-gray-300'
                      }`}
                      placeholder="Enter author name..."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-2">Category</label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-black"
                    placeholder="Enter category..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Content
                    <span className="text-gray-600 text-xs ml-2">
                      ({formData.content.length}/{limits.content})
                    </span>
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    required
                    maxLength={limits.content}
                    rows={12}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 poetry-text text-black ${
                      formData.content.length > limits.content * 0.9 
                        ? 'border-yellow-400 bg-yellow-50' 
                        : 'border-gray-300'
                    }`}
                    placeholder="Enter your poem here... (Use line breaks to separate verses)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-2">Featured Image</label>
                  <div className="space-y-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-black"
                    />
                    {formData.image && (
                      <div className="mt-2">
                        <p className="text-sm text-black mb-2">Preview:</p>
                        <img
                          src={URL.createObjectURL(formData.image)}
                          alt="Preview"
                          className="w-32 h-32 object-cover rounded-lg border"
                        />
                      </div>
                    )}
                    {editingPoem && editingPoem.image && !formData.image && (
                      <div className="mt-2">
                        <p className="text-sm text-black mb-2">Current Image:</p>
                        <img
                          src={getImageUrl(editingPoem.image)}
                          alt="Current"
                          className="w-32 h-32 object-cover rounded-lg border"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-primary-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {isSubmitting 
                      ? (editingPoem ? 'Updating...' : 'Creating...') 
                      : `${editingPoem ? 'Update' : 'Create'} Poem`}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingPoem(null);
                      setFormData({ title: '', content: '', author: '', category: '', image: null });
                      setIsSubmitting(false);
                    }}
                    className="bg-gray-500 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Poems List */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-black">All Poems</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {safePoems.map((poem) => (
                <div key={poem.id} className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row items-start gap-4">
                    {/* Poem Image */}
                    <div className="flex-shrink-0">
                      {getImageUrl(poem.image) ? (
                        <img
                          src={getImageUrl(poem.image)}
                          alt={poem.title}
                          className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center">
                          <span className="text-purple-400 text-xs">Poetry</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Poem Content */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base sm:text-lg font-semibold text-black mb-1">{poem.title}</h4>
                      <p className="text-black text-xs sm:text-sm mb-2">By {poem.author}</p>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-3">
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                          {poem.category}
                        </span>
                        <span className="text-black">
                          {new Date(poem.createdAt?.toDate?.() || poem.createdAt).toLocaleDateString()}
                        </span>
                        <span className="text-gray-600">
                          {poem.content?.length || 0} characters
                        </span>
                      </div>
                      <div className="poetry-text text-black whitespace-pre-line max-h-24 overflow-hidden text-xs sm:text-sm leading-relaxed">
                        {poem.content}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex space-x-2 self-start sm:self-center">
                      <button
                        onClick={() => handleEdit(poem)}
                        className="text-blue-600 hover:text-blue-700 px-2 sm:px-3 py-1 rounded hover:bg-blue-50 transition-colors text-xs sm:text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(poem)}
                        className="text-red-600 hover:text-red-700 px-2 sm:px-3 py-1 rounded hover:bg-red-50 transition-colors text-xs sm:text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {poems.length === 0 && (
                <div className="p-6 text-center text-black">
                  No poems yet. Create your first poem!
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 sm:mt-8">
            <Link
                to="/admin/dashboard"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Dashboard
            </Link>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        {/* Confirmation Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
        />
      </div>
  );
};

const ProtectedManagePoetry = () => (
  <ProtectedRoute>
    <ManagePoetry />
  </ProtectedRoute>
);

export default ProtectedManagePoetry;
